class HallOfFame extends HTMLElement {
  allItems = [];
  renderedCount = 0;
  itemsPerPage = 20;

  isLoading = false;
  observer = null;
  columns = [];
  columnCount = 4;

  constructor() {
    super();
    this.grid = this.querySelector('#masonry-grid');
    this.loader = this.querySelector('#hall-of-fame-loader');
    this.noDataMsg = this.querySelector('.no-data');

    this.metaobjectType = this.dataset.metaobjectType || '';
    this.imageKey = this.dataset.imageKey || 'image';
    this.titleKey = this.dataset.titleKey || 'title';
    this.badgeImage = this.optimizeImage(this.dataset.badgeImage || '', 500);
    this.borderImage = this.optimizeImage(this.dataset.borderImage || '', 500);

    this.storeFrontApiToken = window.huntsman.storeFrontApi;
    this.shopDomain = window.huntsman.domain;

    this.updateColumnCount();

    // Setup matchMedia listeners for breakpoint changes
    this.handleBreakpointChange = () => {
      this.updateColumnCount();
      this.redistributeItems();
    };

    // Define breakpoints: 549px (2↔3 cols) and 990px (3↔4 cols)
    this.mediaQueries = [
      window.matchMedia('(max-width: 549px)'),
      window.matchMedia('(min-width: 990px)'),
    ];
  }

  connectedCallback() {
    if (!this.storeFrontApiToken || !this.shopDomain) {
      console.error('HallOfFame: Storefront API token or Shop Domain missing.');

      return;
    }

    this.initColumns();
    this.initObserver();
    this.fetchAllMetaobjects();

    // Attach matchMedia listeners
    this.mediaQueries.forEach((mq) => {
      mq.addEventListener('change', this.handleBreakpointChange);
    });

    // Add Shopify Theme Editor event listeners if in design mode
    if (window.Shopify?.designMode) {
      this.attachShopifyEventListeners();
    }
  }

  disconnectedCallback() {
    if (this.observer && this.loader) {
      this.observer.unobserve(this.loader);
    }

    // Remove matchMedia listeners
    this.mediaQueries.forEach((mq) => {
      mq.removeEventListener('change', this.handleBreakpointChange);
    });

    // Remove Shopify Theme Editor event listeners
    if (window.Shopify?.designMode) {
      this.detachShopifyEventListeners();
    }
  }

  attachShopifyEventListeners() {
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('shopify:section:unload', this.handleSectionUnload);
  }

  detachShopifyEventListeners() {
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
  }

  handleSectionLoad = (event) => {
    const sectionId = this.closest('.shopify-section')?.id;
    if (event.detail.sectionId === sectionId) {
      this.initObserver();
      this.fetchAllMetaobjects();
    }
  };

  handleSectionUnload = (event) => {
    const sectionId = this.closest('.shopify-section')?.id;
    if (event.detail.sectionId === sectionId && this.observer && this.loader) {
      this.observer.unobserve(this.loader);
    }
  };

  initObserver() {
    if (!this.loader) return;

    // Disconnect existing observer before creating a new one
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !this.isLoading &&
          this.renderedCount < this.allItems.length
        ) {
          this.loadMoreItems();
        }
      },
      { rootMargin: '200px' }
    );

    this.observer.observe(this.loader);
  }

  // Fetch all metaobjects from the Storefront API recursively.
  // We need to fetch everything upfront so we can sort the entire dataset alphabetically on the client side.
  async fetchAllMetaobjects() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.loader?.classList.remove('hidden');

    // Reset state
    this.allItems = [];
    this.renderedCount = 0;

    // Clear grid if re-fetching
    if (this.grid) {
      this.grid.innerHTML = '';
      this.initColumns();
    }

    let hasNext = true;
    let cursor = null;
    const pageSize = 250; // Fetch in large batches

    try {
      while (hasNext) {
        const query = `
          query getMetaobjects($type: String!, $first: Int!, $after: String) {
            metaobjects(type: $type, first: $first, after: $after) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  handle
                  imageField: field(key: "${this.imageKey}") {
                    reference {
                      ... on MediaImage {
                        image {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                  titleField: field(key: "${this.titleKey}") {
                    value
                  }
                }
              }
            }
          }
        `;

        const variables = {
          type: this.metaobjectType,
          first: pageSize,
          after: cursor,
        };

        const response = await fetch(`https://${this.shopDomain}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storeFrontApiToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        const json = await response.json();

        if (json.errors) {
          console.error('HallOfFame: GraphQL Errors:', json.errors);
          break;
        }

        const data = json.data.metaobjects;
        hasNext = data.pageInfo.hasNextPage;
        cursor = data.pageInfo.endCursor;

        const newItems = data.edges.map((edge) => {
          const node = edge.node;

          return {
            id: node.id,
            handle: node.handle,
            image: node.imageField?.reference?.image,
            title: node.titleField?.value || '',
          };
        });

        this.allItems.push(...newItems);
      }

      this.sortItems();

      // Remove initial loading state
      const container = this.querySelector('.masonry-grid-container');
      if (container?.classList.contains('initial-loading')) {
        container.classList.remove('initial-loading');
      }

      this.loadMoreItems();

      if (this.allItems.length === 0) {
        this.noDataMsg?.classList.remove('hidden');
      } else {
        this.noDataMsg?.classList.add('hidden');
      }
    } catch (error) {
      console.error('HallOfFame: Error fetching metaobjects:', error);
    } finally {
      this.isLoading = false;
      this.updateLoaderVisibility();
    }
  }

  // Sort the complete list of items alphabetically by their title.
  sortItems() {
    this.allItems.sort((a, b) => {
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    });
  }

  // Client-side pagination: Take the next chunk of items from the sorted array and render them.
  loadMoreItems() {
    if (this.renderedCount >= this.allItems.length) {
      this.updateLoaderVisibility();

      return;
    }

    const nextItems = this.allItems.slice(
      this.renderedCount,
      this.renderedCount + this.itemsPerPage
    );
    this.renderItems(nextItems);
    this.renderedCount += nextItems.length;
    this.updateLoaderVisibility();
  }

  updateLoaderVisibility() {
    // If the number of items we've rendered equals the total number of items we fetched,
    // then we have shown everything and can hide the loading spinner.
    if (this.renderedCount >= this.allItems.length) {
      this.loader?.classList.add('hidden');
    } else {
      this.loader?.classList.remove('hidden');
    }
  }

  updateColumnCount() {
    const width = window.innerWidth;
    if (width < 550) {
      this.columnCount = 2;
    } else if (width < 800) {
      this.columnCount = 3;
    } else if (width < 990) {
      this.columnCount = 3;
    } else {
      this.columnCount = 4;
    }
  }

  initColumns() {
    if (!this.grid) return;

    // Clear existing columns
    this.grid.innerHTML = '';
    this.columns = [];

    // Create column elements
    for (let i = 0; i < this.columnCount; i++) {
      const column = document.createElement('div');
      column.className = 'masonry-column';
      column.dataset.columnIndex = i;
      this.grid.appendChild(column);
      this.columns.push(column);
    }
  }

  redistributeItems() {
    if (!this.grid) return;

    // Collect all existing items
    const allItems = [];
    this.columns.forEach((column) => {
      const items = Array.from(column.querySelectorAll('.masonry-item'));
      allItems.push(...items);
    });

    // Reinitialize columns with new count
    this.initColumns();

    // Redistribute items
    allItems.forEach((item, index) => {
      const columnIndex = index % this.columnCount;
      this.columns[columnIndex].appendChild(item);
    });
  }

  getShortestColumn() {
    let shortestColumn = this.columns[0];
    let shortestHeight = shortestColumn.offsetHeight;

    this.columns.forEach((column) => {
      const height = column.offsetHeight;
      if (height < shortestHeight) {
        shortestHeight = height;
        shortestColumn = column;
      }
    });

    return shortestColumn;
  }

  optimizeImage(url, width) {
    if (!url) return '';
    // Check if URL already has a size suffix to avoid double resizing
    if (/_\d+x/.test(url)) return url;

    // Insert width suffix before file extension
    return url.replace(/(\.[^.?]+)(\?|$)/, `_${width}x$1$2`);
  }

  debounce(func, wait) {
    let timeout;

    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  renderItems(items) {
    if (!this.grid || !this.columns.length) return;

    items.forEach((item) => {
      if (!item.image) return;

      const div = document.createElement('div');
      div.className = 'masonry-item';

      // Create wrapper for image and border overlay
      const imageWrapper = document.createElement('div');
      imageWrapper.style.position = 'relative';
      imageWrapper.style.lineHeight = '0'; // Remove bottom gap

      const img = document.createElement('img');
      img.src = this.optimizeImage(item.image.url, 500);
      img.alt = item.image.altText || item.title;
      img.width = item.image.width;
      img.height = item.image.height;
      img.loading = 'lazy';
      img.style.width = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.aspectRatio = `${item.image.width} / ${item.image.height}`;
      img.style.backgroundColor = '#f0f0f0';

      imageWrapper.appendChild(img);

      // Apply border image as an overlay if available
      if (this.borderImage) {
        const borderOverlay = document.createElement('img');
        borderOverlay.style.position = 'absolute';
        borderOverlay.style.top = '-7px';
        borderOverlay.style.left = '-7px';
        borderOverlay.style.right = '-7px';
        borderOverlay.style.bottom = '-7px';
        borderOverlay.style.width = 'calc(100% + 14px)';
        borderOverlay.style.height = 'calc(100% + 14px)';
        borderOverlay.style.zIndex = '2';
        borderOverlay.src = this.borderImage;

        imageWrapper.appendChild(borderOverlay);
      }

      const titleContainer = document.createElement('div');
      titleContainer.className = 'title-container';
      titleContainer.style.position = 'relative';

      // Apply badge image as an img tag if available
      if (this.badgeImage) {
        const badgeImg = document.createElement('img');
        badgeImg.src = this.badgeImage;
        badgeImg.alt = '';
        badgeImg.style.position = 'absolute';
        badgeImg.style.top = '0';
        badgeImg.style.left = '0';
        badgeImg.style.width = '100%';
        badgeImg.style.height = '100%';
        badgeImg.style.objectFit = 'fill';
        badgeImg.style.zIndex = '0';
        titleContainer.appendChild(badgeImg);
      }

      const titleText = document.createElement('span');
      titleText.textContent = item.title;
      titleText.style.position = 'relative';
      titleText.style.zIndex = '1';
      titleContainer.appendChild(titleText);

      div.appendChild(imageWrapper);
      div.appendChild(titleContainer);

      // Add to shortest column to maintain balance
      const targetColumn = this.getShortestColumn();
      targetColumn.appendChild(div);
    });
  }
}

if (!customElements.get('hall-of-fame')) {
  customElements.define('hall-of-fame', HallOfFame);
}
